// ============================================================
// MY COURSES — lista corsi acquistati
// ============================================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { courseService } from '../../../../services';
import { formatDuration } from '../../../../constants/mockDashboard';
import type { Course, Progress } from '../../../../types/course';
import './MyCourses.css';

const handlePdfDownload = async (
  fileUrl: string,
  title: string,
  token: string
) => {
  const res = await fetch(fileUrl, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${title}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

interface CourseCardProps {
  course: Course;
  progress: Progress | null;
  authToken: string;
  onOpen: () => void;
}

const CourseCard: React.FC<CourseCardProps> = ({
  course,
  progress,
  authToken,
  onOpen,
}) => {
  const [showPdfs, setShowPdfs] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  const completedCount = progress?.completedLessonIds?.length ?? 0;
  const percent =
    course.lessons.length > 0
      ? Math.round((completedCount / course.lessons.length) * 100)
      : 0;

  const totalSeconds = course.lessons.reduce(
    (s, l) => s + l.durationSeconds,
    0
  );
  const lastLesson = progress?.lessonId
    ? course.lessons.find((l) => l.id === progress.lessonId)
    : null;

  const statusLabel =
    percent === 100 ? 'Completato' : progress ? 'In corso' : 'Non iniziato';
  const statusClass =
    percent === 100
      ? 'mc-status--done'
      : progress
        ? 'mc-status--progress'
        : 'mc-status--new';

  // Raccoglie tutti gli allegati da tutte le lezioni
  const allAttachments = course.lessons.flatMap((l) => l.attachments ?? []);

  return (
    <div
      className="mc-card"
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onOpen()}
    >
      {/* Thumb */}
      <div className="mc-card-thumb">
        {course.imageUrl ? (
          <img src={course.imageUrl} alt={course.title} />
        ) : (
          <div className="mc-card-thumb-placeholder">
            <i className="bi bi-play-circle" />
          </div>
        )}
        {/* Overlay percentuale */}
        {percent > 0 && percent < 100 && (
          <div className="mc-card-thumb-overlay">
            <span>{percent}%</span>
          </div>
        )}
        {percent === 100 && (
          <div className="mc-card-thumb-overlay mc-card-thumb-overlay--done">
            <i className="bi bi-check-circle-fill" />
          </div>
        )}
      </div>

      {/* Contenuto */}
      <div className="mc-card-body">
        <div className="mc-card-top">
          <h2 className="mc-card-title">{course.title}</h2>
          <span className={`mc-status ${statusClass}`}>{statusLabel}</span>
        </div>

        {course.subtitle && <p className="mc-card-sub">{course.subtitle}</p>}

        {/* Stats lezioni */}
        <div className="mc-card-stats">
          <span>
            <i className="bi bi-collection-play me-1" />
            {course.lessons.length} lezioni
          </span>
          <span>
            <i className="bi bi-clock me-1" />
            {formatDuration(totalSeconds)}
          </span>
          {completedCount > 0 && (
            <span>
              <i className="bi bi-check2 me-1" />
              {completedCount}/{course.lessons.length} viste
            </span>
          )}
        </div>

        {/* Barra progresso */}
        <div className="mc-card-progress">
          <div className="mc-progress-bar">
            <div
              className="mc-progress-fill"
              style={{ width: `${percent}%` }}
            />
          </div>
          <span className="mc-progress-pct">{percent}%</span>
        </div>

        {/* Ultima lezione */}
        {lastLesson && (
          <p className="mc-card-last">
            <i className="bi bi-arrow-right-circle me-1" />
            Continua da: <strong>{lastLesson.title}</strong>
          </p>
        )}

        {/* PDF allegati — espandibili */}
        {allAttachments.length > 0 && (
          <div className="mc-card-attachments">
            <button
              className="mc-pdf-toggle"
              onClick={(e) => {
                e.stopPropagation();
                setShowPdfs((v) => !v);
              }}
            >
              <i className="bi bi-paperclip me-1" />
              {allAttachments.length} PDF scaricabili
              <i
                className={`bi ms-1 ${showPdfs ? 'bi-chevron-up' : 'bi-chevron-down'}`}
              />
            </button>

            {showPdfs && (
              <div className="mc-pdf-list" onClick={(e) => e.stopPropagation()}>
                {pdfError && (
                  <p className="mc-pdf-error" role="alert">
                    <i className="bi bi-exclamation-triangle-fill me-1" />
                    {pdfError}
                  </p>
                )}
                {allAttachments.map((att) => (
                  <div key={att.id} className="mc-pdf-item">
                    <i className="bi bi-file-earmark-pdf mc-pdf-icon" />
                    <button
                      className="mc-pdf-title"
                      onClick={async () => {
                        try {
                          await handlePdfDownload(
                            att.fileUrl,
                            att.title,
                            authToken
                          );
                        } catch {
                          setPdfError('Download non riuscito. Riprova.');
                          setTimeout(() => setPdfError(null), 4000);
                        }
                      }}
                      aria-label={`Scarica ${att.title}`}
                    >
                      {att.title}
                    </button>
                    {att.fileSizeKb && (
                      <span className="mc-pdf-size">
                        {att.fileSizeKb >= 1024
                          ? `${(att.fileSizeKb / 1024).toFixed(1)} MB`
                          : `${att.fileSizeKb} KB`}
                      </span>
                    )}
                    <i className="bi bi-download mc-pdf-download-icon" />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Arrow */}
      <div className="mc-card-arrow">
        <i className="bi bi-chevron-right" />
      </div>
    </div>
  );
};

// ─── Main component ───────────────────────────────────────────

const MyCourses: React.FC = () => {
  const navigate = useNavigate();
  const [courses, setCourses] = useState<Course[]>([]);
  const [progressMap, setProgressMap] = useState<
    Record<string, Progress | null>
  >({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const authToken = localStorage.getItem('bp_token') ?? '';

  useEffect(() => {
    const load = async () => {
      try {
        const fetched = await courseService.getMyCourses();
        setCourses(fetched);
        const entries = await Promise.all(
          fetched.map(
            async (c) =>
              [c.id, await courseService.getProgress(c.id)] as [
                string,
                Progress | null,
              ]
          )
        );
        setProgressMap(Object.fromEntries(entries));
      } catch {
        setLoadError(true);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const completedCount = courses.filter((c) => {
    const p = progressMap[c.id];
    return (
      p &&
      c.lessons.length > 0 &&
      (p.completedLessonIds?.length ?? 0) === c.lessons.length
    );
  }).length;

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '60vh' }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Caricamento...</span>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div
        className="d-flex flex-column align-items-center justify-content-center gap-3 text-center"
        style={{ minHeight: '60vh' }}
        role="alert"
      >
        <i className="bi bi-wifi-off fs-1 text-body-secondary" />
        <p className="fw-semibold mb-0">Impossibile caricare i corsi</p>
        <p className="text-body-secondary small mb-0">
          Controlla la connessione e riprova.
        </p>
        <button
          className="btn btn-primary btn-sm mt-1"
          onClick={() => window.location.reload()}
        >
          Ricarica pagina
        </button>
      </div>
    );
  }

  return (
    <div className="mc-page">
      {/* Header pagina */}
      <div className="mc-header">
        <div>
          <h1 className="mc-title">I miei corsi</h1>
          <p className="mc-subtitle">
            {courses.length === 0
              ? 'Non hai ancora acquistato nessun corso'
              : `${courses.length} cors${courses.length === 1 ? 'o' : 'i'} acquistati · ${completedCount} completati`}
          </p>
        </div>
      </div>

      {/* Lista corsi */}
      {courses.length === 0 ? (
        <div className="mc-empty">
          <i className="bi bi-cart-plus mc-empty-icon" />
          <p className="mc-empty-title">Nessun corso ancora</p>
          <p className="mc-empty-sub">
            Esplora i nostri corsi e inizia il tuo percorso di benessere.
          </p>
          <button
            className="mc-empty-cta"
            onClick={() => navigate('/rimettiti-al-centro')}
          >
            <i className="bi bi-arrow-right me-2" />
            Scopri i corsi
          </button>
        </div>
      ) : (
        <div className="mc-list">
          {courses.map((course) => (
            <CourseCard
              key={course.id}
              course={course}
              progress={progressMap[course.id] ?? null}
              authToken={authToken}
              onOpen={() => navigate(`/dashboard/corsi/${course.id}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default MyCourses;
